/**
 * Un solo formato di errore, per tutte le rotte del livello GateMesh
 * (05-CONTRATTO-API §10).
 *
 * `code` per comporre la frase nella lingua del dispositivo; `params` per i numeri dentro
 * la frase; `retryable` perché l'app deve sapere se ha senso riprovare da sola. Un errore
 * senza `retryable` diventa un riprova-sempre o un non-riprovare-mai, e sono sbagliati
 * entrambi.
 *
 * **Il server manda codici, la frase la compone il player** (14-LINGUE-E-TESTI §3). Se il
 * server mandasse una frase italiana, un televisore turco mostrerebbe italiano, e non c'è
 * modo di rimediare lato app.
 */

/** I codici che il gateway può mandare, più quelli che nascono sull'apparecchio. */
export const ERROR_CODES = [
  // Accesso — 05-CONTRATTO-API §3 e §3.1
  'gateway.unreachable',
  'gateway.backend_unavailable',
  'auth.missing_credentials',
  'auth.invalid_token',
  'auth.invalid_credentials',
  'auth.wrong_gateway',
  'auth.refused',
  'line.expired',
  'line.disabled',
  'line.banned',
  'line.max_connections_reached',
  // Dati della linea — 05-CONTRATTO-API §4.0
  'app.unknown_section',
  // Vetrina ed eventi — 05-CONTRATTO-API §5.1 e §6.2
  'app.storefront_unavailable',
  'app.unknown_event_kind',
  // Aggregatori. I due casi sono separati perché uno è ritentabile e l'altro no: «non è ancora
  // pubblicato» passa da sé, «non è tuo» no — e un'app che riprovasse il secondo insisterebbe per
  // sempre su una porta che non si aprirà.
  'app.aggregator_unavailable',
  'app.unknown_aggregator',
  // Riproduzione — 06-RIPRODUZIONE §6
  'stream.start_timeout',
  /*
    Il canale c'è a catalogo e **non è in onda**: spento, in manutenzione, senza sessione.

    Separato da `start_timeout` perché i due hanno rimedi opposti: un avvio lento si riprova, un
    canale non attivo no — riprovare per quindici secondi un canale in manutenzione fa aspettare per
    una cosa che non succederà, e poi dice «riprova fra poco» a chi dovrebbe sceglierne un altro.
  */
  'stream.not_available',
  /* Il canale esiste ma non è nel bouquet della linea: non è un guasto, è una vendita. */
  'stream.not_in_subscription',
  'stream.source_dropped',
  'stream.decoder_unsupported',
  'catchup.not_available',
  // Apparecchio
  'device.offline',
  'device.request_timeout',
  /*
    **L'archivio dell'apparecchio è pieno.** Il catalogo è arrivato; è conservarlo che non è riuscito.

    Il 30-09-2026 su `stream.opik.net` la schermata dei film diceva «non riusciamo a caricare il
    catalogo — `app.malformed_response`», e il catalogo era perfetto: 6.826 film e 27 categorie, JSON
    valido, letti senza una protesta dai nostri stessi lettori. A non stare in piedi era
    `localStorage`: 11.212 canali, 6.826 film e 2.396 serie fanno **6,82 milioni di caratteri** contro
    un tetto di circa cinque milioni per origine. I canali si scrivono all'avvio; poi si apre «Film» e
    la scrittura solleva.

    Un codice suo perché il rimedio è di un'altra natura — non si ritenta, non si cambia indirizzo,
    non si riguarda il gateway: si fa spazio, o si cambia archivio. E perché il codice sbagliato è
    costato un giro di diagnosi passato a cercare un carattere che rompesse la compatibilità dentro un
    dato che non aveva niente che non andasse.
  */
  'device.storage_full',
  /*
    Qualcosa è andato storto e **non sappiamo cosa**: lo diciamo invece di indovinare.

    Esiste per non far più finire i guasti sconosciuti in `app.malformed_response`. Quello nomina una
    causa — «il server ha risposto qualcosa di storto» — e mandava a cercare nel posto sbagliato ogni
    volta che il guasto era nostro e stava sull'apparecchio.
  */
  'app.unexpected_failure',
  // Forma della risposta non riconoscibile: rumoroso di proposito, mai ingoiato.
  'app.malformed_response',
  /*
    All'indirizzo configurato **non c'è un gateway**: ha risposto una pagina HTML.

    Separato da `malformed_response` perché la diagnosi è di un'altra natura, e questa è la sola
    che porti a una correzione. «La risposta è malformata» manda a cercare un difetto nel gateway;
    invece non c'è nessun gateway — c'è un server di sviluppo senza proxy, la pagina d'errore di un
    reverse proxy, un portale di rete, o il pannello al posto dell'API. Nessun gateway risponde HTML
    a `player_api.php`, quindi non è un'ipotesi: è un fatto che si può dire.

    Il 29-09-2026 è costato un giro di diagnosi con davanti «non riusciamo a caricare il catalogo»,
    che di quell'indirizzo non diceva niente.
  */
  'app.not_a_gateway',
  // Il gemello, dalla parte opposta: il gateway non ha riconosciuto la **richiesta**. È sempre un
  // difetto dell'app, non del cliente, e per questo va detto con un codice invece di diventare un
  // «non parte» — chi legge la schermata di diagnosi deve poter dire quale parametro.
  'app.malformed_request',
  // L'app ha chiesto qualcosa che il contratto col gateway non copre ancora. Nasce
  // sull'apparecchio, non dal server, e non si nasconde: un buco del contratto che si
  // manifesta come «non parte» costa più di un errore con un nome.
  'app.unsupported_by_contract',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export type ErrorParams = Readonly<Record<string, string | number>>;

export interface GatewayError {
  readonly code: ErrorCode | string;
  readonly params: ErrorParams;
  /** A quale dei quattro passi dell'accesso si è fermato (02-ACCESSO §2). */
  readonly failedStep?: number;
  readonly retryable: boolean;
  /** L'istante in cui è successo: va mostrato in piccolo insieme al codice, per chi
   *  risponde al telefono (06-RIPRODUZIONE §6). */
  readonly atMs: number;
}

export class PlayerError extends Error {
  readonly detail: GatewayError;

  constructor(detail: GatewayError) {
    super(detail.code);
    this.name = 'PlayerError';
    this.detail = detail;
  }
}

export function isKnownErrorCode(code: string): code is ErrorCode {
  return (ERROR_CODES as ReadonlyArray<string>).includes(code);
}

/**
 * Legge il corpo di un errore del gateway.
 *
 * Un corpo che non ha questa forma **non** diventa «credenziali errate» e non diventa un
 * errore generico muto: diventa `app.malformed_response`, che è ritentabile e si vede.
 * Accusare il cliente di un caso che non abbiamo previsto è il modo di trasformare un
 * nostro difetto nella sua mezz'ora persa (05-CONTRATTO-API §3.1).
 */
export function parseGatewayError(body: unknown, atMs: number): GatewayError {
  if (typeof body !== 'object' || body === null) {
    return { code: 'app.malformed_response', params: {}, retryable: true, atMs };
  }
  const record = body as Record<string, unknown>;
  if (typeof record['code'] !== 'string' || record['code'].length === 0) {
    return { code: 'app.malformed_response', params: {}, retryable: true, atMs };
  }
  const params: Record<string, string | number> = {};
  const rawParams = record['params'];
  if (typeof rawParams === 'object' && rawParams !== null) {
    for (const [key, value] of Object.entries(rawParams as Record<string, unknown>)) {
      if (typeof value === 'string' || typeof value === 'number') params[key] = value;
    }
  }
  const error: GatewayError = {
    code: record['code'],
    params,
    retryable: record['retryable'] === true,
    atMs,
  };
  return typeof record['failedStep'] === 'number'
    ? { ...error, failedStep: record['failedStep'] }
    : error;
}

/**
 * Un rifiuto esplicito del server è l'unica cosa che riporta alla schermata di accesso.
 *
 * **Un errore di rete non è un rifiuto** (02-ACCESSO §4.2): se il gateway non risponde si
 * riprova e si continua con la cache; non si butta fuori nessuno perché il Wi-Fi ha
 * singhiozzato.
 */
export function sendsBackToLogin(code: string): boolean {
  return (
    code === 'auth.invalid_token' ||
    code === 'auth.invalid_credentials' ||
    code === 'auth.missing_credentials' ||
    code === 'auth.wrong_gateway' ||
    code === 'line.expired' ||
    code === 'line.disabled' ||
    code === 'line.banned'
  );
}

/**
 * Quando le credenziali conservate vanno **cancellate**, e non solo rifiutate.
 *
 * È un insieme più stretto di `sendsBackToLogin`, e la differenza è tutta la ragione per cui questa
 * funzione esiste. Con l'accesso per codice non si rientra riscrivendo un token: serve un
 * rivenditore raggiungibile che scansioni (SPEC §9). Quindi cancellare per uno stato **reversibile**
 * — linea scaduta, sospesa, disattivata — punisce chi rinnova in ritardo obbligandolo a ri-attivare
 * l'apparecchio, quando invece basterebbe riaccenderlo il giorno dopo.
 *
 * Si cancella solo quando la credenziale in sé è morta e non tornerà: il gettone è stato rigenerato,
 * o la linea è passata a un altro gateway. Lì tenerla servirebbe a ritentare per sempre qualcosa che
 * non funzionerà mai più.
 */
export function forgetsCredentials(code: string): boolean {
  return (
    code === 'auth.invalid_token' ||
    code === 'auth.invalid_credentials' ||
    code === 'auth.missing_credentials' ||
    code === 'auth.wrong_gateway'
  );
}

/**
 * Il dettaglio di **qualunque cosa** sia stata lanciata, senza inventare la causa.
 *
 * ## Perché non `parseGatewayError(null, …)`
 *
 * Era quel che si usava in ogni `catch`, e restituisce sempre `app.malformed_response`. Per un corpo
 * d'errore che non ha la forma attesa è la risposta giusta; per un `catch` qualunque è una **causa
 * inventata**: dice che il server ha risposto qualcosa di storto anche quando il server non c'entra.
 *
 * Il 30-09-2026 è costato un giro di diagnosi. La schermata dei film diceva «non riusciamo a caricare
 * il catalogo — `app.malformed_response`» e il sospetto è caduto sui nomi delle categorie, che hanno
 * emoji e trattini lunghi. Il catalogo era perfetto: a sollevare era `localStorage`, pieno.
 *
 * ## L'archivio pieno ha quattro forme
 *
 * `QuotaExceededError` è il nome standard; Firefox usa `NS_ERROR_DOM_QUOTA_REACHED`; i browser più
 * vecchi mandano **solo il numero**, `22` di norma e `1014` su Firefox. Si guardano tutte e quattro —
 * e sono proprio i browser vecchi a contare, perché l'app gira su WebView congelate: un elenco che
 * comprendesse il solo nome standard non riconoscerebbe l'archivio pieno esattamente dove capita di più.
 */
export function thrownDetail(error: unknown, atMs: number): GatewayError {
  if (error instanceof PlayerError) return error.detail;
  if (isStorageFull(error)) {
    // Non ritentabile: riprovare a scrivere in un archivio pieno riempie l'archivio pieno. Serve un
    // gesto — fare spazio — e dirlo ritentabile farebbe girare l'app a vuoto invece di dirlo.
    return { code: 'device.storage_full', params: {}, retryable: false, atMs };
  }
  return { code: 'app.unexpected_failure', params: {}, retryable: true, atMs };
}

/** Se quel che è stato lanciato è un archivio locale pieno. Vedi `thrownDetail`. */
export function isStorageFull(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as { name?: unknown; code?: unknown };
  return (
    candidate.name === 'QuotaExceededError' ||
    candidate.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    candidate.code === 22 ||
    candidate.code === 1014
  );
}
